"use client";

import { useEffect, useState } from "react";

import { fetchSuggestedSellers } from "@/lib/profile/suggested-sellers";
import type { ProfileSuggestedUser } from "@kraahl/contract";

/**
 * Request phase for the suggested-seller list.
 *
 * A union, not `sellers` + `loading` + `error` side by side: those three can
 * express states the fetch never reaches (loading WITH an error, ready with no
 * rows AND still loading) and every consumer then re-derives "am I allowed to
 * read `sellers` yet". Here `sellers` only exists on the branch that has them
 * (`react-state-design` §4).
 *
 * There is no `idle`: the hook fires on mount, so the first observable state is
 * already `loading`.
 */
export type SuggestedSellersState =
  | { kind: "loading" }
  | { kind: "ready"; sellers: ProfileSuggestedUser[] }
  | { kind: "error" };

export interface UseSuggestedSellersArgs {
  /** Profile to leave out — the viewed seller on a profile page, the viewer on the feed. */
  excludeProfileId?: string | null;
  /** Route clamps to 1..20; omit to take its default of 12. */
  limit?: number;
}

/**
 * Suggested sellers for a discovery surface (feed right rail, profile follow
 * carousel), fetched through the canonical builder.
 *
 * `AbortController`, not an ignore-flag: `excludeProfileId` changes when the
 * viewer's session resolves a beat after mount, so a superseded request would
 * otherwise stay in flight and land on top of the fresh one
 * (`react-state-design` §10). An aborted fetch rejects — the `AbortError`
 * lands in the same `catch`, and the guard below keeps it from flipping a
 * newer request's state to `error`.
 *
 * Failure is silent by design: both call sites render nothing when the list is
 * empty, so a discovery surface that could not load simply is not there. The
 * builder still throws (typed `ApiResponseParseError` on a wire mismatch) —
 * suppression is this hook's policy decision, not the adapter's.
 *
 * @see lib/profile/suggested-sellers.ts — the builder + its schema
 */
export function useSuggestedSellers({
  excludeProfileId,
  limit,
}: UseSuggestedSellersArgs = {}): SuggestedSellersState {
  const [state, setState] = useState<SuggestedSellersState>({ kind: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    setState({ kind: "loading" });

    fetchSuggestedSellers({ excludeProfileId, limit, signal: controller.signal })
      .then((sellers) => {
        if (!controller.signal.aborted) setState({ kind: "ready", sellers });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ kind: "error" });
      });

    return () => controller.abort();
  }, [excludeProfileId, limit]);

  return state;
}
